import React, { useEffect, useState } from "react";
import { Bell, BellOff, X } from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE } from "@/lib/api";

/**
 * PWANotificationPrompt
 * -----------------------------------------------------------------
 * When the CRM is opened as an installed PWA (iPhone home screen, Android home
 * screen, or Chrome desktop standalone), Safari / Chrome do NOT autotriggered a
 * permission prompt, the site has to ask. This component pops a friendly panel
 * once per install so the advisor can turn on lead alerts with one tap.
 *
 * Rules:
 * - Only shows when `display-mode: standalone` (real PWA install, not a browser tab)
 * - Only shows when `Notification.permission === "default"`
 * - Only shows once per install (dismiss OR grant sets a flag)
 * - Silently no-op if the browser doesn't support Notifications / PushManager
 */
function _urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
  return arr;
}

async function subscribeToPush() {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    const keyRes = await api.get("/push/vapid-public-key");
    const vapidKey = keyRes.data?.key;
    if (!keyRes.data?.configured || !vapidKey) return false;
    const existing = await reg.pushManager.getSubscription();
    const sub = existing || (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: _urlBase64ToUint8Array(vapidKey),
    }));
    const json = sub.toJSON ? sub.toJSON() : JSON.parse(JSON.stringify(sub));
    await api.post("/push/subscribe", json);
    return true;
  } catch {
    return false;
  }
}

export default function PWANotificationPrompt() {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const decide = () => {
      try {
        if (typeof Notification === "undefined") return;
        if (Notification.permission !== "default") return;
        const standalone = window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
        const iosStandalone = window.navigator.standalone === true;
        if (!standalone && !iosStandalone) return;
        if (localStorage.getItem("pwa_notif_prompt_seen") === "1") return;
        setVisible(true);
      } catch { /* noop */ }
    };
    // Delay so we don't fight the first-paint animation.
    const to = setTimeout(decide, 900);
    return () => clearTimeout(to);
  }, []);

  const grant = async () => {
    setBusy(true);
    try {
      let perm = Notification.permission;
      if (perm !== "granted") {
        perm = await Notification.requestPermission();
      }
      if (perm !== "granted") {
        toast.error("Notifications blocked. You can turn them on later from CRM → Leads → Enable alerts.");
        localStorage.setItem("pwa_notif_prompt_seen", "1");
        setVisible(false);
        return;
      }
      const ok = await subscribeToPush();
      if (ok) {
        toast.success("Alerts on. New leads will notify this device even when closed.");
      } else {
        try { new Notification("Propertic CRM ready", { body: "In-tab alerts are on.", icon: "/propertic-logo.png" }); } catch {}
        toast.success("Alerts on for this session.");
      }
      localStorage.setItem("pwa_notif_prompt_seen", "1");
      setVisible(false);
    } catch {
      toast.error("Could not enable alerts. Try again from CRM → Leads.");
    } finally { setBusy(false); }
  };

  const dismiss = () => {
    try { localStorage.setItem("pwa_notif_prompt_seen", "1"); } catch {}
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-notif-prompt"
      className="fixed inset-x-4 bottom-4 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:max-w-sm z-[80] rounded-2xl bg-propertic-navy text-propertic-paper shadow-2xl border border-propertic-gold/40 p-5"
    >
      <button onClick={dismiss} data-testid="pwa-notif-dismiss" aria-label="Close" className="absolute top-3 right-3 text-white/60 hover:text-white">
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <div className="w-10 h-10 rounded-full bg-propertic-gold/20 grid place-items-center text-propertic-gold shrink-0">
          <Bell className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <p className="font-mono-brand text-[10px] tracking-[0.2em] uppercase text-propertic-gold mb-1">New device · Enable alerts</p>
          <p className="font-editorial text-lg leading-tight">Get pinged when a lead lands.</p>
          <p className="text-[12px] text-white/70 mt-1.5 leading-relaxed">
            Turn on push notifications so this iPhone / device buzzes the moment a new consultation or loan request comes in, even when the app is closed.
          </p>
        </div>
      </div>
      <div className="mt-4 flex items-center gap-2">
        <button
          onClick={grant}
          disabled={busy}
          data-testid="pwa-notif-grant"
          className="flex-1 h-11 rounded-full bg-propertic-gold text-propertic-navy text-[11px] font-mono-brand tracking-[0.18em] uppercase inline-flex items-center justify-center gap-2 hover:bg-propertic-gold-2 transition-colors disabled:opacity-60"
        >
          <Bell className="w-3.5 h-3.5" /> {busy ? "…" : "Allow notifications"}
        </button>
        <button
          onClick={dismiss}
          data-testid="pwa-notif-later"
          className="h-11 px-4 rounded-full border border-white/20 text-white/70 text-[11px] font-mono-brand tracking-[0.18em] uppercase inline-flex items-center gap-1.5 hover:bg-white/10 transition-colors"
        >
          <BellOff className="w-3.5 h-3.5" /> Later
        </button>
      </div>
    </div>
  );
}

// Ensure the API_BASE import doesn't get tree-shaken warnings.
export const _b = API_BASE;
